import differenceBy from "lodash/differenceBy";
import { findIndex, filter, groupBy } from "lodash";
import { documentValidation } from "./multiDocumentValidaton";
import { COMPANY_DOCUMENTS, STAKEHOLDER_DOCUMENTS } from "../constants/index";

// Detects older prospect structure for stakeholder KYC docs
// Older prospects store combined type 'Passport_Visa_EID' with specific uniqueIds
export const isOlderProspectKyc = uploadedDoc => {
  if (!uploadedDoc) return false;
  const isOlderProspectType = uploadedDoc.documentType === "Passport_Visa_EID";
  const olderIds = ["DOC39", "DOC98", "DOC40", "DOC41"];
  return isOlderProspectType && olderIds.includes(uploadedDoc.uniqueId);
};

export const concatCompanyDocs = (
  existDocs,
  incomeDocs,
  organizationInfo,
  organizationInfoRO,
  orgKYCDetails,
  signatories
) => {
  const companyDocsDiff = differenceBy(incomeDocs, existDocs, "uniqueId");
  let newDocs = [...(existDocs || []), ...companyDocsDiff];
  newDocs = validateDocuments(
    newDocs,
    organizationInfo,
    organizationInfoRO,
    orgKYCDetails,
    signatories
  );
  return newDocs;
};

export const concatStakeholdersDocs = (
  neededDocs,
  uploadedDocs,
  organizationInfo,
  organizationInfoRO,
  orgKYCDetails,
  signatories
) => {
  // Build stakeholders docs from neededDocs, merging prospectDocument from uploadedDocs by uniqueId only
  return Object.entries(neededDocs || {}).reduce((acc, [signatoryId, { documents }]) => {
    const uploadedDocuments = uploadedDocs[signatoryId] ? uploadedDocs[signatoryId].documents : [];
    const neededDocuments = neededDocs[signatoryId] ? neededDocs[signatoryId].documents : [];

    // For each needed document, if there's an uploaded doc with same uniqueId, merge its prospectDocument.
    const mergedDocs = (neededDocuments || []).map(rawNeededDoc => {
      const neededDoc = {
        ...rawNeededDoc,
        prospectDocument: Array.isArray(rawNeededDoc.prospectDocument)
          ? rawNeededDoc.prospectDocument
          : []
      };
      const uploadedMatch = uploadedDocuments.find(u => u.uniqueId === neededDoc.uniqueId);
      if (
        uploadedMatch &&
        Array.isArray(uploadedMatch.prospectDocument) &&
        isOlderProspectKyc(uploadedMatch)
      ) {
        return {
          ...uploadedMatch,
          prospectDocument: uploadedMatch.prospectDocument.map(p => ({
            ...p,
            documentTitle: neededDoc.groupTitle
          })),
          groupTitle: neededDoc.groupTitle,
          documentPosition: neededDoc.documentPosition
        };
      }
      const uploadedMatchForNew = uploadedDocuments.find(
        u => u.uniqueId === neededDoc.uniqueId && u.groupTitle === neededDoc.groupTitle
      );
      if (uploadedMatchForNew) {
        return {
          ...neededDoc,
          prospectDocument: uploadedMatchForNew.prospectDocument.map(p => ({
            ...p,
            documentTitle: neededDoc.groupTitle
          }))
        };
      }
      return neededDoc;
    });

    const validated = validateDocuments(
      mergedDocs,
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories,
      signatoryId
    );

    acc[signatoryId] = { documents: validated };
    return acc;
  }, {});
};

export const createDocumentMapper = (
  documentType,
  documentKey,
  documentTitle,
  additionalProps
) => docs => {
  if (docs.documentType === documentType) {
    docs.prospectDocument = docs.prospectDocument.map(doc => {
      doc.documentTitle = doc.documentTitle.replace("amp;", "&");
      // Match by documentKey and optionally by documentTitle (if provided)
      const titleMatches = !documentTitle || doc.documentTitle === documentTitle;
      if (doc.documentKey === documentKey && titleMatches) {
        return { ...doc, ...additionalProps };
      }
      return doc;
    });
  }
  return docs;
};

export const appendDocumentKey = docs =>
  (docs || []).map((doc, index) => {
    const docIndex = docs
      .slice(0, index)
      .filter(document => document.documentType === doc.documentType).length;
    return { ...doc, documentKey: `${doc.documentType}-${docIndex}` };
  });

export const appendMultiDocumentKey = docs => {
  if (docs) {
    docs.prospectDocument = docs.prospectDocument || [];
    docs.prospectDocument = (docs.prospectDocument || []).map((doc, index) => {
      const docIndex = docs.prospectDocument
        .slice(0, index)
        .filter(document => document.documentType === doc.documentType).length;
      return {
        ...doc,
        documentKey: `${docs.documentType}-${docIndex}`,
        documentTitle: docs.groupTitle,
        documentType: docs.documentType
      };
    });
  }
  return docs;
};

export const validateDocuments = (
  docs,
  organizationInfo,
  organizationInfoRO,
  orgKYCDetails,
  signatories,
  signatoryId = null
) => {
  docs.map(doc => {
    doc.required = documentValidation(
      doc,
      organizationInfo,
      organizationInfoRO,
      orgKYCDetails,
      signatories,
      signatoryId
    );
    doc.groupTitle = doc.groupTitle.replace("amp;", "&");
    return doc;
  });
  return docs;
};

export const range = (end, start = 0) => Array.from({ length: end - start }, (_, i) => start + i);

export const getDocumentByUniqueId = (documents, uniqueId, stakeholderIndex) => {
  if (stakeholderIndex) {
    const docIndex = findIndex(documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]["documents"], {
      uniqueId
    });
    return {
      docIndex,
      documents: documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]["documents"][docIndex]
    };
  } else {
    const docIndex = findIndex(documents[COMPANY_DOCUMENTS], {
      uniqueId
    });
    return {
      docIndex,
      documents: documents[COMPANY_DOCUMENTS][docIndex]
        ? documents[COMPANY_DOCUMENTS][docIndex]
        : {}
    };
  }
};

// Find stakeholder document by uniqueId and parent groupTitle to disambiguate variants
export const getStakeholderDocumentByUniqueIdAndTitle = (
  documents,
  uniqueId,
  stakeholderIndex,
  groupTitle
) => {
  if (stakeholderIndex) {
    const list = documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]
      ? documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]["documents"]
      : [];
    const docIndex = list.findIndex(d => d.uniqueId === uniqueId && d.groupTitle === groupTitle);
    return {
      docIndex,
      documents: list[docIndex]
    };
  }
  const docIndex = findIndex(documents[COMPANY_DOCUMENTS], {
    uniqueId,
    groupTitle
  });
  return {
    docIndex,
    documents: documents[COMPANY_DOCUMENTS][docIndex] ? documents[COMPANY_DOCUMENTS][docIndex] : {}
  };
};

export const findAgendDocument = (documents, uniqueId, stakeholderIndex = "") => {
  if (stakeholderIndex) {
    if (documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]) {
      const docIndex = findIndex(documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]["documents"], {
        uniqueId
      });
      return {
        docIndex,
        documents: documents[STAKEHOLDER_DOCUMENTS][stakeholderIndex]["documents"][docIndex]
      };
    } else {
      return {
        docIndex: -1,
        documents: {}
      };
    }
  } else {
    const docIndex = findIndex(documents[COMPANY_DOCUMENTS], { uniqueId });
    return {
      docIndex,
      documents: documents[COMPANY_DOCUMENTS][docIndex]
        ? documents[COMPANY_DOCUMENTS][docIndex]
        : {}
    };
  }
};

export const isAgendDocumentUploaded = (documents, uniqueId) => {
  let uploaded = false;
  const docIndex = findIndex(documents, { uniqueId });
  uploaded = documents[docIndex]?.prospectDocument[0]?.uploadStatus === "Uploaded";
  return uploaded;
};

export const getGroupedDocuments = (docs, docPosition) => {
  docs = filter(docs, { documentPosition: docPosition });
  return groupBy(docs, "groupKey");
};

export const isRequiredDocsUploaded = documents => {
  const isRequiredUploaded = doc => {
    let flag = false;
    if (doc.required === false) {
      flag = true;
    } else if (doc.prospectDocument[0].uploadStatus === "Uploaded") {
      flag = true;
    }
    return flag;
  };

  return documents.every(doc => isRequiredUploaded(doc));
};

export const isAllDocsUploaded = documents => {
  const isAllUploaded = doc => {
    return doc.prospectDocument[0].uploadStatus === "Uploaded" ? true : false;
  };

  return documents.every(doc => isAllUploaded(doc));
};

/**
 * Generates a unique document identifier for uploads and validation
 *
 * @param {string|number} stakeholderIndex - Index of the stakeholder (optional)
 * @param {string} uniqueId - Unique identifier for the document category
 * @param {string} documentKey - Document key identifier
 * @returns {string} Formatted document identifier
 */
export const generateDocumentIdentifier = (stakeholderIndex, uniqueId, documentKey) => {
  return stakeholderIndex
    ? `${stakeholderIndex}_${uniqueId}_${documentKey}`
    : `${uniqueId}_${documentKey}`;
};
